import React, { useState, useEffect, useRef } from 'react';
import Popup from '../../index';
import { recordingConfirmPopupDisplay } from '../../../../../actions/popupActions';
import { findContacts } from '../../../../../actions/contactActions';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { findContactDetails } from '../../../../../actions/contactDetailsActions';
import axios from 'axios';
import { API_ROOT } from '../../../../utils';
import './index.css';

export default function RecordingConfirmPopup({ mode }) {
	const modeType = (typeof mode === 'object' && mode !== null) ? mode.mode : mode;
	const fileRefId = (typeof mode === 'object' && mode !== null) ? mode.fileRefId : null;

	const [opStatus, setOpStatus] = useState(fileRefId ? 'pending' : 'legacy_countdown');
	const [errorMessage, setErrorMessage] = useState(null);
	const [countdown, setCountdown] = useState(10);
	const [showClose, setShowClose] = useState(false);
	const [pollCount, setPollCount] = useState(0);

	const selectedContactsGroup = useSelector(state => state.contactsGroupItemReducer.getSelectedContactsGroup);
	const contactsPageSize = useSelector(state => state.contactReducer.contactsPageSize);
	const contactDetails = useSelector(state => state.contactDetailsReducer.findContactDetails);

	const dispatch = useDispatch();
	const { t } = useTranslation();
	const pollTimerRef = useRef(null);

	// Real status polling when fileRefId is available
	useEffect(() => {
		if (!fileRefId) return;

		let isMounted = true;
		const MAX_POLLS = 20; // 20 * 1200ms ≈ 24s

		const checkStatus = async () => {
			try {
				const response = await axios.get(`${API_ROOT}/api/v1/file/${fileRefId}/status`, {
					withCredentials: true
				});

				if (!isMounted) return;

				const data = response.data || {};
				const currentStatus = data.status;

				if (currentStatus === 'SUCCESS') {
					setOpStatus('success');
					setShowClose(true);
					if (modeType === 'create') {
						dispatch(findContacts(selectedContactsGroup ? selectedContactsGroup.id : '6', contactsPageSize));
					} else if (contactDetails && contactDetails.id) {
						dispatch(findContactDetails(contactDetails.id, contactDetails.phoneContactEntry.type));
					}
					return;
				}

				if (currentStatus === 'FAILED') {
					setOpStatus('failed');
					setShowClose(true);
					setErrorMessage(data.errorReason || null);
					return;
				}

				setPollCount(prev => {
					const nextCount = prev + 1;
					if (nextCount >= MAX_POLLS) {
						setOpStatus('timeout');
						setShowClose(true);
					} else {
						pollTimerRef.current = setTimeout(checkStatus, 1200);
					}
					return nextCount;
				});
			} catch (err) {
				console.error("Error polling AI operation status:", err);
				if (!isMounted) return;
				setPollCount(prev => {
					const nextCount = prev + 1;
					if (nextCount >= MAX_POLLS) {
						setOpStatus('timeout');
						setShowClose(true);
					} else {
						pollTimerRef.current = setTimeout(checkStatus, 1500);
					}
					return nextCount;
				});
			}
		};

		pollTimerRef.current = setTimeout(checkStatus, 1200);

		return () => {
			isMounted = false;
			if (pollTimerRef.current) {
				clearTimeout(pollTimerRef.current);
			}
		};
	}, [fileRefId, modeType, dispatch, selectedContactsGroup, contactsPageSize, contactDetails]);

	// Fallback countdown for legacy triggers without fileRefId
	useEffect(() => {
		if (fileRefId) return;

		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
			return () => clearTimeout(timer);
		} else if (countdown === 0 && !showClose) {
			if (modeType === 'create') {
				dispatch(findContacts(selectedContactsGroup ? selectedContactsGroup.id : '6', contactsPageSize));
			} else if (contactDetails && contactDetails.id) {
				dispatch(findContactDetails(contactDetails.id, contactDetails.phoneContactEntry.type));
			}
			setOpStatus('success');
			setShowClose(true);
		}
	}, [fileRefId, countdown, showClose, modeType, dispatch, selectedContactsGroup, contactsPageSize, contactDetails]);

	const handleClose = () => {
		dispatch(recordingConfirmPopupDisplay(null));
	};

	const progress = fileRefId
		? Math.min(100, Math.round((pollCount / 15) * 100))
		: ((10 - countdown) / 10) * 100;

	return (
		<Popup unClosable={!showClose} className="p-4 sm-popup" onClick={ handleClose }>
			<div className={`gauge${!showClose ? ' in-progress' : ''}`}>

				<svg width="100" height="100" viewBox="0 0 100 100" className="position-relative" style={{ zIndex: 2 }}>
					<circle cx="50" cy="50" r="45" fill="none" stroke="#e9ecef" strokeWidth="4" />
					<circle
						cx="50"
						cy="50"
						r="45"
						fill="none"
						stroke={opStatus === 'failed' ? '#dc3545' : 'url(#gradient)'}
						strokeWidth="4"
						strokeLinecap="round"
						strokeDasharray={`${2 * Math.PI * 45}`}
						strokeDashoffset={2 * Math.PI * 45 * (1 - (showClose ? 100 : progress) / 100)}
						transform="rotate(-90 50 50)"
						style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
					/>
					<defs>
						<linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
							<stop offset="0%" stopColor="#007bff" />
							<stop offset="100%" stopColor="#6f42c1" />
						</linearGradient>
					</defs>
				</svg>

				<div className="position-absolute" style={{ zIndex: 3 }}>
					<div className={!showClose ? 'swing' : ''} style={{ fontSize: '2.5rem', color: opStatus === 'failed' ? '#dc3545' : '#007bff' }}>
						{opStatus === 'failed' ? '⚠️' : opStatus === 'timeout' ? '⏳' : '👤'}
					</div>
				</div>
				<div className="position-absolute" style={{width:100, height:100}}>
					{ !showClose &&
					[...Array(6)].map((_, i) => (
						<div
							key={i}
							className="position-absolute sparkle"
							style={{
								left: `${36 + 66 * Math.cos((i * Math.PI * 2) / 6)}px`,
								top: `${33 + 66 * Math.sin((i * Math.PI * 2) / 6)}px`,
								animationDelay: `${i * 0.2}s`,
							}}
						>
							<span style={{ color: '#ffc107', fontSize: '1.5rem' }}>✨</span>
						</div>
					))
					}
				</div>
			</div>

			<div className="text-center">
				{opStatus === 'failed' ? (
					<>
						<h5 className="text-danger mb-3">
							{modeType === 'create'
								? t('recordingConfirmPopupContactCreationError', 'Contact creation failed')
								: t('recordingConfirmPopupContactUpdateError', 'Contact update failed')}
						</h5>
						<p className="text-muted fade-in-up">
							{errorMessage || t('recordingConfirmPopupErrorReason', 'An unexpected error occurred during processing.')}
						</p>
					</>
				) : opStatus === 'timeout' ? (
					<>
						<h5 className="text-dark mb-3">
							{t('recordingConfirmPopupTakingLongerTitle', 'Traitement en cours...')}
						</h5>
						<p className="text-muted fade-in-up">
							{t('recordingConfirmPopupTakingLongerDesc', 'Le traitement prend plus de temps que prévu. Votre contact apparaîtra dans votre liste dès qu’il sera prêt.')}
						</p>
					</>
				) : modeType === 'create' ? (
					<>
						<h5 className={`text-dark mb-3 ${!showClose ? 'pulse-text' : ''}`}>
							{showClose
								? t('recordingConfirmPopupContactCreationTitle')
								: t('recordingConfirmPopupContactCreationPending')}
						</h5>
						{!showClose ? (
							<div className="text-muted fade-in">
								⚡ {t('recordingConfirmPopupContactCreationInProgress')}
							</div>
						) : (
							<p className="text-muted fade-in-up">
								{t('recordingConfirmPopupContactAdded')}
							</p>
						)}
					</>
				) : (
					<>
						<h5 className={`text-dark mb-3 ${!showClose ? 'pulse-text' : ''}`}>
							{showClose
								? t('recordingConfirmPopupContactUpdateTitle')
								: t('recordingConfirmPopupContactUpdatePending')}
						</h5>
						{!showClose ? (
							<div className="text-muted fade-in">
								⚡ {t('recordingConfirmPopupContactUpdateInProgress')}
							</div>
						) : (
							<p className="text-muted fade-in-up">
								{t('recordingConfirmPopupContactUpdated')}
							</p>
						)}
					</>
				)}
				{showClose && (
					<button
						className="btn btn-danger mt-2 fade-in-up"
						onClick={ handleClose }
					>
						{t('closeBtn')}
					</button>
				)}
			</div>
		</Popup>
	);
}
